// Statistik: Hörer über die Zeit, beste Uhrzeiten, Sendungen, Titel, Streams.

import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { errorText } from "../components/streaming-ui";
import { Callout, Card, Page, PageHeader, Skeleton, StatTile, Tabs } from "../components/ui";
import type { Channel } from "../types";

type Overview = Awaited<ReturnType<typeof api.getStatsOverview>>;
const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function Timeline({ points }: { points: Overview["timeline"] }): JSX.Element {
  const width = 800;
  const height = 180;
  const max = Math.max(1, ...points.map((p) => p.peak));
  if (points.length < 2) return <div className="flex h-44 items-center justify-center rounded-lg border border-dashed border-default text-sm text-muted">Noch zu wenig Messwerte – die Suite zählt jede Minute.</div>;
  const t0 = new Date(points[0]!.hour).getTime();
  const t1 = new Date(points[points.length - 1]!.hour).getTime();
  const x = (iso: string): number => ((new Date(iso).getTime() - t0) / Math.max(1, t1 - t0)) * (width - 40) + 30;
  const y = (v: number): number => height - 20 - (v / max) * (height - 30);
  const line = (key: "avg" | "peak"): string => points.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.hour).toFixed(1)},${y(p[key]).toFixed(1)}`).join(" ");
  // Fläche unter dem Durchschnitt – leiser Verlauf in der Hausfarbe.
  const area = `${line("avg")} L${x(points[points.length - 1]!.hour).toFixed(1)},${y(0).toFixed(1)} L${x(points[0]!.hour).toFixed(1)},${y(0).toFixed(1)} Z`;
  const muted = { fill: "var(--text-muted)", fontSize: 10 };
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-48 w-full" role="img" aria-label="Hörer im Zeitverlauf" preserveAspectRatio="none">
      <defs>
        <linearGradient id="stats-flaeche" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgb(var(--brand-primary-rgb))" stopOpacity={0.22} />
          <stop offset="100%" stopColor="rgb(var(--brand-primary-rgb))" stopOpacity={0} />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={30} x2={width - 10} y1={y(max * f)} y2={y(max * f)} strokeDasharray={f === 0 ? undefined : "3 4"} style={{ stroke: "var(--border)" }} />
          <text x={2} y={y(max * f) + 4} style={muted} className="tabular-nums">{Math.round(max * f)}</text>
        </g>
      ))}
      <path d={area} fill="url(#stats-flaeche)" />
      <path d={line("peak")} fill="none" className="stroke-amber-400" strokeWidth={1.25} strokeDasharray="4 3" strokeLinejoin="round" />
      <path d={line("avg")} fill="none" className="stroke-brand-primary" strokeWidth={2.25} strokeLinejoin="round" strokeLinecap="round" />
      <text x={30} y={height - 4} style={muted}>{new Date(t0).toLocaleDateString("de-DE")}</text>
      <text x={width - 10} y={height - 4} textAnchor="end" style={muted}>{new Date(t1).toLocaleDateString("de-DE")}</text>
    </svg>
  );
}

function WeekGrid({ grid }: { grid: Overview["week_grid"] }): JSX.Element {
  const max = Math.max(0.1, ...grid.flat().map((v) => v ?? 0));
  return (
    <div className="overflow-x-auto">
      <table className="border-separate border-spacing-[3px] text-[10px]">
        <thead>
          <tr>
            <th />
            {Array.from({ length: 24 }, (_, h) => <th key={h} className="w-5 !text-[10px] !font-normal !normal-case !tracking-normal tabular-nums">{h % 3 === 0 ? h : ""}</th>)}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, d) => (
            <tr key={d} className="hover:!bg-transparent">
              <td className="pr-1.5 font-medium text-muted">{WEEKDAYS[d]}</td>
              {row.map((v, h) => (
                <td key={h} title={v === null ? "keine Messung" : `${WEEKDAYS[d]} ${h}–${h + 1} Uhr: ⌀ ${v} Hörer`}
                  className="h-5 w-5 rounded" style={{ background: v === null ? "var(--bg-sunken)" : `rgb(var(--brand-primary-rgb) / ${(0.08 + (v / max) * 0.9).toFixed(3)})` }} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatsPage(): JSX.Element {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState("");
  const [days, setDays] = useState(7);
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listChannels().then((c) => {
      setChannels(c);
      setSlug((s) => s || c[0]?.slug || "");
    }).catch((err) => setError(errorText(err)));
  }, []);
  useEffect(() => {
    if (!slug) return;
    setData(null);
    api.getStatsOverview(slug, days).then(setData).catch((err) => setError(errorText(err)));
  }, [slug, days]);

  const best = useMemo(() => {
    if (!data) return null;
    let top: { d: number; h: number; v: number } | null = null;
    data.week_grid.forEach((row, d) => row.forEach((v, h) => {
      if (v !== null && (!top || v > top.v)) top = { d, h, v };
    }));
    return top as { d: number; h: number; v: number } | null;
  }, [data]);

  return (
    <Page>
      <PageHeader
        eyebrow="Auswerten"
        title="Statistik"
        description="Hörer über die Zeit, beste Uhrzeiten, Sendungen, Titel und Streams."
        actions={
          <>
            {channels.length > 1 && (
              <select value={slug} onChange={(e) => setSlug(e.target.value)} className="input !w-auto">
                {channels.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            )}
            <Tabs
              value={String(days)}
              onChange={(v) => setDays(Number(v))}
              items={[1, 7, 30, 90].map((d) => ({ value: String(d), label: d === 1 ? "24 Stunden" : `${d} Tage` }))}
            />
          </>
        }
      />
      <div className="space-y-5">
      {error && <Callout tone="danger">{error}</Callout>}
      {!data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>
          <Skeleton className="h-60" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile label="⌀ Hörer" value={data.summary.avg_listeners.toLocaleString("de-DE")} tone="brand" />
            <StatTile
              label="Spitze"
              value={data.summary.peak_listeners}
              hint={data.summary.peak_at ? new Date(data.summary.peak_at).toLocaleString("de-DE", { weekday: "short", hour: "2-digit", minute: "2-digit" }) : undefined}
            />
            <StatTile label="Hörstunden" value={data.summary.listener_hours.toLocaleString("de-DE")} />
            <StatTile label="Beste Zeit" value={<span className="text-xl">{best ? `${WEEKDAYS[best.d]} ${best.h}–${best.h + 1} Uhr` : "–"}</span>} />
          </div>
          <Card
            title="Hörer im Verlauf"
            actions={
              <div className="flex items-center gap-3 text-xs text-muted">
                <span className="inline-flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded bg-brand-primary" />Durchschnitt pro Stunde</span>
                <span className="inline-flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded bg-amber-400" />Spitze</span>
              </div>
            }
          >
            <Timeline points={data.timeline} />
          </Card>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card title="Wann hören die meisten zu?">
              <WeekGrid grid={data.week_grid} />
            </Card>
            <Card title="Womit wird gehört?">
              {data.streams.length === 0 ? <p className="text-sm text-muted">Noch keine Hörer gemessen.</p> : (
                <ul className="space-y-3">
                  {data.streams.map((s) => (
                    <li key={s.label} className="text-sm">
                      <div className="mb-1 flex justify-between text-secondary"><span>{s.label}</span><span className="font-medium tabular-nums text-primary">{s.share.toLocaleString("de-DE")} %</span></div>
                      <div className="h-2 overflow-hidden rounded-full bg-input ring-1 ring-[var(--border)]"><div className="h-2 rounded-full bg-brand-primary" style={{ width: `${s.share}%` }} /></div>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-xs text-muted">Hörer über HLS (Browser, Handys) zählt kein Server mit; sie fehlen hier.</p>
            </Card>
            <Card title="Sendungen" padded={data.shows.length === 0}>
              {data.shows.length === 0 ? <p className="text-sm text-muted">Keine Sendungen im Sendeplan oder noch keine Messwerte zu ihren Zeiten.</p> : (
                <table className="w-full text-sm"><tbody>
                  {data.shows.map((s) => <tr key={s.title} className="[&>td]:border-b [&>td]:border-default [&:last-child>td]:border-0"><td className="px-5 py-2.5 text-secondary">{s.title}</td><td className="px-5 py-2.5 text-right font-medium tabular-nums text-primary">⌀ {s.avg_listeners.toLocaleString("de-DE")}</td></tr>)}
                </tbody></table>
              )}
            </Card>
            <Card title="Meistgespielte Titel" padded={data.top_tracks.length === 0}>
              {data.top_tracks.length === 0 ? <p className="text-sm text-muted">Noch nichts gespielt.</p> : (
                <table className="w-full text-sm"><tbody>
                  {data.top_tracks.map((t, i) => (
                    <tr key={`${t.title}-${t.artist}`} className="[&>td]:border-b [&>td]:border-default [&:last-child>td]:border-0">
                      <td className="w-8 py-2.5 pl-5 text-xs tabular-nums text-muted">{i + 1}</td>
                      <td className="max-w-0 truncate py-2.5 pr-3 text-secondary">{[t.artist, t.title].filter(Boolean).join(" – ")}</td>
                      <td className="py-2.5 text-right tabular-nums text-muted">{t.plays}×</td>
                      <td className="w-24 py-2.5 pr-5 text-right font-medium tabular-nums text-primary">{t.avg_listeners === null ? "" : `⌀ ${t.avg_listeners}`}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}
            </Card>
          </div>
        </>
      )}
      </div>
    </Page>
  );
}
